<template>
  <ul
    class="frisbuy-customer-review__media-list"
    :style="{
      flexWrap: props.wrap,
      gap: props.gap,
    }"
  >
    <li
      v-for="(media, idx) in props.mediaList.slice(0, props.maxMediaItems)"
      :key="idx"
      class="frisbuy-customer-review__media-list-item"
      :style="{
        backgroundImage: `url(${DomainSubstitute(
          media.thumbnailUrl
        )}), url(${errorImagePlug})`,
        width: props.mediaWidth,
        height: props.mediaHeight,
        aspectRatio: props.mediaAspectRatio,
      }"
      @click.stop="() => selectMedia(media)"
    >
      <img
        class="frisbuy-customer-review__media-image"
        :src="DomainSubstitute(media.thumbnailUrl)"
        @error="errorImageHandler"
      />

      <span
        v-if="
          media.videoUrl.length > 0 &&
          !(
            props.isShowCountHover &&
            idx + 1 === props.maxMediaItems &&
            hiddenMediaCount > 0
          )
        "
        class="frisbuy-customer-review__play-icon"
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22.9748 13.7289C24.6669 14.7701 24.6669 17.2297 22.9748 18.271L13.3963 24.1655C11.6196 25.2589 9.33203 23.9806 9.33203 21.8944L9.33203 10.1054C9.33203 8.01926 11.6196 6.74099 13.3963 7.83436L22.9748 13.7289Z"
            fill="white"
          />
        </svg>
      </span>
      <div
        v-if="
          props.isShowCountHover &&
          idx + 1 === props.maxMediaItems &&
          hiddenMediaCount > 0
        "
        class="frisbuy-customer-review__media-list-item-hover-count"
      >
        <div class="frisbuy-customer-review__media-list-item-hover-count-text">
          +{{ hiddenMediaCount }}
        </div>
      </div>
    </li>
    <li
      v-if="props.isShowCountBlock && hiddenMediaCount > 0"
      class="frisbuy-customer-review__media-list-item-counter-block"
      :style="{
        width: props.mediaWidth,
        height: props.mediaHeight,
        aspectRatio: props.mediaAspectRatio,
      }"
      @click.stop="() => selectMedia(props.mediaList[props.maxMediaItems + 1])"
    >
      +{{ hiddenMediaCount }}
    </li>
  </ul>
</template>

<script setup lang="ts">
// Modules
import { computed, onMounted, nextTick } from "vue";
import { Property } from "csstype";
// Services
import { MediaCatcher } from "@/common/services/ErrorHandler/ErrorCatcher";
import { DomainSubstitute } from "@/common/services/DomainSubstitute/DomainSubstitute";
// Consts
import widgetTypes from "@/common/consts/widgetTypes";
// Types
import { imageWithPostId } from "@/widgets/MarketplaceReviews/interfaces";

export type MediaListCustomization = {
  wrap?: Property.FlexWrap;
  gap?: Property.Gap;
  mediaWidth?: Property.Width;
  mediaHeight?: Property.Height;
  maxMediaItems?: number;
  isShowCountBlock?: boolean;
  isShowCountHover?: boolean;
  mediaAspectRatio?: Property.AspectRatio;
  totalMediaCount?: number;
};

const props = withDefaults(
  defineProps<
    {
      mediaList: Array<imageWithPostId>;
    } & MediaListCustomization
  >(),
  {
    // Styles
    wrap: "nowrap",
    gap: "8px",
    mediaWidth: "80px",
    mediaHeight: "unset",
    mediaAspectRatio: "1 / 1",
    maxMediaItems: 6,
    isShowCountBlock: false,
    isShowCountHover: false,
  }
);

const hiddenMediaCount = computed(() => {
  const totalMediaCount = props.totalMediaCount ?? props.mediaList.length;
  return Math.max(totalMediaCount - props.maxMediaItems, 0);
});

const emit = defineEmits<{
  (e: "selectMedia", media: imageWithPostId): void;
}>();

function selectMedia(media: imageWithPostId) {
  emit("selectMedia", media);
}

// Safari fix for video height issue
function fixSafariVideoHeight() {
  if (
    typeof window !== "undefined" &&
    /Safari/.test(navigator.userAgent) &&
    !/Chrome/.test(navigator.userAgent)
  ) {
    nextTick(() => {
      const mediaItems = document.querySelectorAll(
        ".frisbuy-customer-review__media-list-item"
      );
      mediaItems.forEach((item: Element) => {
        const htmlItem = item as HTMLElement;
        // Force reflow by toggling display
        const originalDisplay = htmlItem.style.display;
        htmlItem.style.display = "none";
        htmlItem.offsetHeight; // Force reflow
        htmlItem.style.display = originalDisplay || "flex";
      });
    });
  }
}

function errorImageHandler(e: Event) {
  MediaCatcher(e, 2, widgetTypes.STORY);
}

onMounted(() => {
  fixSafariVideoHeight();
});

const errorImagePlug = "https://www.frisbuy.ru/images/empty_media.png";
</script>

<style scoped lang="scss">
.frisbuy-customer-review__media-list {
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  display: flex;
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;

  list-style: none;
  width: 100%;
  min-width: 0;

  padding: 0;
  margin: 0;
}

.frisbuy-customer-review__media-list-item {
  display: inline-block;
  position: relative;

  width: 80px;

  border-radius: 8px;
  flex: 0 0 auto;

  background-size: cover;
  background-position: center;

  cursor: pointer;

  .frisbuy-customer-review__media-image {
    display: none;

    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .frisbuy-customer-review__play-icon {
    position: absolute;
    inset: 50%;
    transform: translate(-50%, -50%);

    width: 32px;
    height: 32px;

    z-index: 1;

    svg {
      width: 32px;
      height: 32px;
    }
  }
}

.frisbuy-customer-review__media-list-item-counter-block {
  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--frisbuy-color-primary-4);
  border-radius: 8px;

  color: var(--frisbuy-color-primary);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  cursor: pointer;
}

.frisbuy-customer-review__media-list-item-hover-count {
  position: absolute;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
  background-color: #21212199;
  border-radius: 8px;

  color: var(--frisbuy-color-white);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}
</style>
